/**
 * Pannello dei messaggi programmati della chat (sopra il campo di scrittura):
 * elenco, modifica di testo e ora, annullamento con controllo dell'esito.
 * Estratto da MessageInput per tenerlo leggibile.
 */

import { useState, useCallback, useEffect } from 'react';
import { CalendarClock, ChevronDown, Pencil, Trash2, Check, X as XIcon, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { iconTip } from '@/components/messages/ChatTooltip';

interface ScheduledMsg {
  id: string;
  content: string;
  scheduled_at: string;
}

interface ScheduledMessagesPanelProps {
  chatId: string;
  organizationId?: string;
  /** Cambia quando si programma un nuovo messaggio: si ricarica l'elenco. */
  reloadKey: number;
}

export function ScheduledMessagesPanel({ chatId, organizationId, reloadKey }: ScheduledMessagesPanelProps) {
  const [scheduledMsgs, setScheduledMsgs] = useState<ScheduledMsg[]>([]);
  const [scheduledExpanded, setScheduledExpanded] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editTime, setEditTime] = useState('');

  // Carica messaggi programmati per questa chat
  const fetchScheduled = useCallback(() => {
    if (!organizationId || !chatId) return;
    supabase
      .from('whatsapp_scheduled_messages')
      .select('id, content, scheduled_at')
      .eq('organization_id', organizationId)
      .eq('chat_jid', chatId)
      .eq('status', 'scheduled')
      .order('scheduled_at', { ascending: true })
      .then(({ data }) => setScheduledMsgs(data || []));
  }, [chatId, organizationId]);

  useEffect(() => { fetchScheduled(); }, [fetchScheduled, reloadKey]);

  const handleDeleteScheduled = useCallback(async (id: string) => {
    setDeletingId(id);
    // .select(): senza righe restituite l'annullamento non e' avvenuto (RLS o gia' inviato).
    const { data, error } = await supabase
      .from('whatsapp_scheduled_messages')
      .update({ status: 'cancelled' })
      .eq('id', id)
      .eq('status', 'scheduled')
      .select('id');
    setDeletingId(null);
    setConfirmDeleteId(null);
    if (error || !data || data.length === 0) {
      console.error('[ScheduledMessagesPanel] annullamento programmato non riuscito:', error);
      toast.error('Non sono riuscito ad annullare il messaggio programmato: forse e\' gia\' partito. Controlla la chat.');
      fetchScheduled();
      return;
    }
    setScheduledMsgs((prev) => prev.filter((m) => m.id !== id));
    toast.success('Messaggio programmato annullato');
  }, [fetchScheduled]);

  const handleSaveEdit = useCallback(async (id: string, origScheduledAt: string) => {
    const [h, m] = editTime.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    const base = new Date(origScheduledAt);
    base.setHours(h, m, 0, 0);
    const { error } = await supabase
      .from('whatsapp_scheduled_messages')
      .update({ content: editContent, scheduled_at: base.toISOString() })
      .eq('id', id);
    if (error) {
      toast.error('Modifica del messaggio programmato non riuscita. Riprova.');
      return;
    }
    setEditingId(null);
    fetchScheduled();
  }, [editContent, editTime, fetchScheduled]);

  if (scheduledMsgs.length === 0) return null;

  return (
      <div className="border-b border-[rgba(55,53,47,0.06)] dark:border-white/[0.06]">
        <button
          onClick={() => setScheduledExpanded((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-1.5 hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03] transition-colors text-left"
        >
          <CalendarClock className="w-3.5 h-3.5 text-[#2383E2] flex-shrink-0" />
          <span className="flex-1 text-[11px] text-[#2383E2] font-medium">
            {scheduledMsgs.length === 1 ? '1 messaggio programmato' : `${scheduledMsgs.length} messaggi programmati`}
          </span>
          <ChevronDown className={`w-3 h-3 text-[#A4A4A0] transition-transform duration-150 ${scheduledExpanded ? '' : '-rotate-90'}`} />
        </button>

        {scheduledExpanded && (
          <div className="px-4 pb-2 space-y-1.5">
            {scheduledMsgs.map((msg) => {
              const d = new Date(msg.scheduled_at);
              const timeStr = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
              const dateStr = d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
              const isEditingMsg = editingId === msg.id;
              const isConfirmDelete = confirmDeleteId === msg.id;

              return (
                <div key={msg.id} className="bg-[rgba(55,53,47,0.024)] dark:bg-white/[0.03] rounded-lg border border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] p-2.5">
                  {isEditingMsg ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={2}
                        className="w-full text-[11px] text-[#37352F] dark:text-white bg-white dark:bg-[#191919] border border-[rgba(55,53,47,0.1)] dark:border-white/10 rounded-md px-2.5 py-1.5 resize-none outline-none focus:border-[#2383E2] transition-colors"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                          className="text-[11px] text-[#37352F] dark:text-white bg-white dark:bg-[#191919] border border-[rgba(55,53,47,0.1)] dark:border-white/10 rounded-md px-2 py-1 outline-none focus:border-[#2383E2]"
                        />
                        <div className="flex-1" />
                        <button onClick={() => setEditingId(null)} className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06]"><XIcon className="w-3.5 h-3.5 text-[#A4A4A0]" /></button>
                        <button onClick={() => handleSaveEdit(msg.id, msg.scheduled_at)} className="p-1 rounded-md bg-[#2383E2] hover:bg-[#1a6fc2]"><Check className="w-3.5 h-3.5 text-white" /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-[#37352F] dark:text-white/90 line-clamp-2">{msg.content}</p>
                        <p className="text-[10px] text-[#2383E2] font-medium mt-0.5">{dateStr} alle {timeStr}</p>
                      </div>
                      {isConfirmDelete ? (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[9px] text-red-500">Annullare?</span>
                          <button onClick={() => handleDeleteScheduled(msg.id)} disabled={deletingId === msg.id} className="p-1 rounded-md bg-red-500 hover:bg-red-600 disabled:opacity-50" {...iconTip('Si, annulla l\'invio')}>{deletingId === msg.id ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <Check className="w-3 h-3 text-white" />}</button>
                          <button onClick={() => setConfirmDeleteId(null)} className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06]" {...iconTip('No, tienilo')}><XIcon className="w-3 h-3 text-[#A4A4A0]" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => { setEditingId(msg.id); setEditContent(msg.content); setEditTime(new Date(msg.scheduled_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })); }}
                            className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06]"
                            {...iconTip('Modifica testo e ora')}
                          ><Pencil className="w-3 h-3 text-[#A4A4A0]" /></button>
                          <button onClick={() => setConfirmDeleteId(msg.id)} className="p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20" {...iconTip('Annulla l\'invio programmato')}><Trash2 className="w-3 h-3 text-[#A4A4A0] hover:text-red-500" /></button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
  );
}
